/**
 * FormSelect component for rendering select fields with Formik.
 * This includes the label, the select itself and error message.
 *
 * ## Example usage:
 *
 *   ...
 */
import React from 'react'
import { ErrorMessage, Field } from 'formik'
import { Tooltip } from '../Tooltip'
import { TreeCombobox, TreeNode } from '../../components/ui/tree-combobox'
import { useTranslation } from 'react-i18next'
import { useUserStore } from '../../../shared/store/userStore'

type ClassificationCodeInputProps = {
    name: string
    label?: string
    placeholder?: string
    required?: boolean
    tooltip?: string | React.ReactNode
}

export function ClassificationCodeInput({ label, name, tooltip, placeholder, required = false, ...props }: ClassificationCodeInputProps) {
    const { t } = useTranslation()
    const user = useUserStore(state => state.user)
    const [data, setData] = React.useState<TreeNode[]>([])
    const [loading, setLoading] = React.useState(true)

    React.useEffect(() => {
        // there's only EN and HR languages, if it's explicilty set to EN, load EN, else load HR in any other case
        let url = user.UiLanguage === 'en' ? '/other/classification_codes_en.json' : '/other/classification_codes_hr.json'

        fetch(url)
            .then(response => {
                return response.json()
            })
            .then(json => {
                setData(json)
                setLoading(false)
            })
            .catch(err => {
                console.error('Failed to get/parse classification codes', err)
            })
    }, [user.UiLanguage])

    return (
        <div className="v2-root v2-component flex flex-col gap-1.5">
            <div className="flex gap-1 items-center w-full">
                {label && (
                    <label htmlFor={name} className="text-grey100 font-sm-bold">
                        {label}
                    </label>
                )}
                {required ? <span className="text-red20">*</span> : null}
                {tooltip ? <Tooltip>{tooltip}</Tooltip> : null}
            </div>

            <Field name={name}>
                {({ field, form }: any) => {
                    const hasError = form.errors[name] && form.touched[name]
                    return loading ? (
                        <div>{t('Allaround.Message.loading')}</div>
                    ) : (
                        <TreeCombobox
                            data={data}
                            name={name}
                            value={field.value}
                            onValueChange={value => {
                                form.setFieldValue(field.name, value)
                            }}
                            placeholder={placeholder}
                            error={hasError}
                        />
                    )
                }}
            </Field>

            <ErrorMessage name={name} component="span" className="text-sm text-red20" />
        </div>
    )
}
